/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useDebouncedValue } from '@mantine/hooks'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { useLoadMore, type LoadMoreInfo } from '../../../hooks/useLoadMore'
import { useRouterRef } from '../../../hooks/useRouterRef'
import { groupBy } from '../../../libs/groupBy'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/filter/[handler]'
import type { FacetFilter } from '../../backend/typesense/FacetFilter'
import type { ListProductOrder } from '../../const/ListProductOrder'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ProductBannerResult } from '../../data/ProductFragment'
import type { HiddenProductFilter } from '../../filter/HiddenProductFilter'
import { createEmptyProductFilter } from '../../filter/productFilterUtils'
import { serializeFilter } from '../../filter/serializeFilter'
import SvgCross from '../../svg/icons/Cross'
import { useActiveDog, useDER, useDERCalculator } from '../../user/ActiveDogContext'
import { useAuth } from '../../user/UserContext'
import { addDataLayer } from '../../utils/addDataLayer'
import { addDataLayerRangeFilter } from '../../utils/addRangeDataLayer'
import type { DogInfoSchema } from '../dogForm/DogInfoSchema'
import { CountIndicator } from '../form/CheckboxRegular'
import { Switch } from '../form/Switch'
import { ProductBanner } from '../product/ProductBanner'
import { Button } from '../shared/Button'
import { Collapsible } from '../shared/Collapsible'
import { Chevron } from './Chevron'
import { FilterCheckbox } from './FilterCheckbox'
import { FilterRange } from './FilterRange'
import style from './ProductFilterControl.module.sass'

const api = createFetcher<API>('/api/filter/[handler]')

export function cleanupProductFilter(filter: FacetFilter) {
	const update = Object.fromEntries(Object.entries(filter).filter(([_, value]) => !!value))

	if (JSON.stringify(update) !== JSON.stringify(filter)) {
		return update
	}
}

type ProductQuery = {
	filter: FacetFilter
	hiddenFilter?: HiddenProductFilter
	baseUrl?: string
}

export function useProductFilterQuery(query: ProductQuery, page = 1) {
	const { filter, hiddenFilter, baseUrl } = query
	return useQuery({
		queryKey: ['productFilter', filter, hiddenFilter, page, baseUrl],
		queryFn: async ({ signal }) => {
			return api(
				'filter',
				{
					page,
					filter,
					hiddenFilter,
					baseUrl,
					items: true,
					groups: true,
					options: false,
				},
				signal,
			)
		},
		placeholderData: keepPreviousData,
	})
}

type DogQuery = {
	dogInfo?: DogInfoSchema
}

export function useBestProductsTotalCount(query: DogQuery) {
	const { dogInfo } = query
	return useQuery({
		queryKey: ['bestProductsTotalCount', dogInfo],
		queryFn: async () => {
			return api('bestProductsTotalCount', { dogInfo })
		},
		placeholderData: keepPreviousData,
	})
}

export function useTopAndFedProductAllIds(query: DogQuery) {
	/* signed out view */
	const { dogInfo } = query
	return useQuery({
		queryKey: ['topAndFedProduct', dogInfo],
		queryFn: async () => {
			return api('topAndFedProduct', { dogInfo })
		},
		placeholderData: keepPreviousData,
	})
}

export function useAllIds(query: DogQuery) {
	const { dogInfo } = query
	return useQuery({
		queryKey: ['allProductIds', dogInfo],
		queryFn: async () => {
			return api('allProductIds', { dogInfo })
		},
		placeholderData: keepPreviousData,
	})
}

export function useProductFilterOptions(query: ProductQuery) {
	return useProductFilterQuery(query, 1)
}

export function useProductFilterLoadMoreQuery(
	query: ProductQuery,
	initialData?: LoadMoreInfo<ProductBannerResult>,
	order?: ListProductOrder,
) {
	const { filter, hiddenFilter, baseUrl } = query
	return useLoadMore<ProductBannerResult>(
		['productFilter', filter, hiddenFilter, baseUrl, order],
		initialData ?? null,
		async (page) =>
			api('filter', {
				page,
				filter,
				hiddenFilter,
				baseUrl,
				items: true,
				options: false,
				groups: false,
				order,
			}) as Promise<LoadMoreInfo<ProductBannerResult>>,
	)
}

function useProductFilter(initialFilter: FacetFilter, hiddenFilter?: HiddenProductFilter, baseUrl?: string) {
	const [filter, setFilter] = useState<FacetFilter>(initialFilter)

	const [debouncedFilter] = useDebouncedValue(filter, 150)

	const query: ProductQuery = {
		filter: debouncedFilter,
		hiddenFilter,
		baseUrl,
	}

	const info = useProductFilterOptions(query)

	const filterUrl = info.data?.url

	useEffect(() => {
		const dirty = cleanupProductFilter(filter)

		if (dirty) {
			setFilter(dirty)
		}
	}, [filter])

	return { filter, setFilter, debouncedFilter, info, query, filterUrl }
}

function usePropagateFilterUrl(url: string | undefined) {
	const router = useRouterRef()

	useEffect(() => {
		if (url) {
			router.current.replace(url, undefined, { shallow: true, scroll: false })
		}
	}, [url, router])
}

export function useProductFilterControl(
	initialFilter: FacetFilter,
	hiddenFilter?: HiddenProductFilter,
	baseUrl?: string,
) {
	const [page, setPage] = useState(1)

	const { filter, setFilter, info, query, filterUrl, debouncedFilter } = useProductFilter(
		initialFilter,
		hiddenFilter,
		baseUrl,
	)

	useEffect(() => {
		setPage(1)
	}, [debouncedFilter])

	usePropagateFilterUrl(filterUrl)

	const isDirty = (key: keyof FacetFilter) => Boolean(info.data && info.data?.filter?.[key] !== filter[key])

	const DER = useDER().data?.dailyEnergyRequirement

	const [openToggles, setOpenToggles] = useState<Record<string, boolean>>({})

	const toggleOpen = useCallback((key: string) => {
		return setOpenToggles((old) => ({ ...old, [key]: !old[key] }))
	}, [])

	const { myDog, ...filterRest } = filter
	const filterConditional = !!serializeFilter(filterRest) || myDog === 'true'
	const [isFiltered, setIsFiltered] = useState(filterConditional)

	useEffect(() => {
		if (filterConditional) {
			setIsFiltered(true)
		} else setIsFiltered(false)
	}, [filter, filterConditional, filterRest])

	const resetFilter = useCallback(
		(key?: keyof FacetFilter) => {
			setFilter((old) => {
				const emptyFilter = createEmptyProductFilter()
				if (key) {
					return { ...old, [key]: emptyFilter[key] }
				}
				return emptyFilter
			})
		},
		[setFilter],
	)

	return {
		page,
		setPage,
		filter,
		setFilter,
		groups: info.data?.groups,
		DER,
		isFiltered,
		info,
		query,
		openToggles,
		toggleOpen,
		isDirty,
		resetFilter,
	}
}

export type ProductFilterControl = ReturnType<typeof useProductFilterControl>

export type ProductFilterControlSection =
	| 'flavors'
	| 'price'
	| 'score'
	| 'pricePerDose'
	| 'brands'
	| 'nutritionalValues'
	| 'treatments'
	| 'breeds'
	| 'allergies'
	| 'ageCategories'
	| 'others'

export function ProductFilterControlPanel(props: {
	control: ProductFilterControl
	ignoreSections?: Array<ProductFilterControlSection>
	openModal?: () => void
	isLocked?: boolean
}) {
	const { filter, setFilter, groups, info, openToggles, toggleOpen, isFiltered, resetFilter } = props.control

	if (info.isLoading) {
		return null
	}

	const translate = useTranslate()
	const auth = useAuth()
	const { dog } = useActiveDog()

	const isAllowedToFullUse = useIsAllowedToFullUse()
	const notSigned = auth.status !== 'signedIn'

	const calculator = useDERCalculator()

	const content = (
		<div className={clsx(style.root, (!isAllowedToFullUse || notSigned) && style.locked)}>
			<div className={style.header}>
				<h2 className={style.rootTitle}>
					<Translation tr="filterControl.title" fallback="Filtry" />
				</h2>
				{isFiltered && !props.isLocked && (
					<Button
						iconBefore={<SvgCross />}
						icon={{ small: true }}
						label={translate('filterControl.removeAll')}
						type={{ variant: 'naked' }}
						onClick={() => resetFilter()}
					/>
				)}
			</div>
			<div className={style.content}>
				{groups?.map((group) => {
					type Group = typeof group
					const renderControl = (control: Group['controls'][number]) => {
						switch (control.type) {
							case 'Range':
							case 'CheckboxRange': {
								const filterValue = rangeToMinMax(filter[control.field])
								const serverValue = rangeToMinMax(info.data?.filter[control.field])
								const value = filterValue ?? control.range ?? null

								const localCheckboxValue = !!filterValue || !!(control.checkboxField && filter[control.checkboxField])
								const serverCheckboxValue =
									!!filterValue || !!(control.checkboxField && info.data?.filter[control.checkboxField])

								const visibleRange = control.range ?? control.globalRange ?? null

								const checkbox = control.type === 'CheckboxRange' && (
									<FilterCheckbox
										spacing={0}
										name={control.field}
										value={'true'}
										label={control.label ?? control.field}
										checked={localCheckboxValue}
										onChange={(checked) =>
											setFilter((o) => {
												const patch = {
													...o,
													...(control.checkboxField ? { [control.checkboxField]: 'true' } : {}),
													[control.field]: [visibleRange?.min ?? null, visibleRange?.max ?? null] as [
														number | null,
														number | null,
													],
												}

												if (!checked) {
													if (control.checkboxField) {
														delete patch[control.checkboxField]
													}
													delete patch[control.field]
												}

												return patch
											})
										}
										dirty={localCheckboxValue !== serverCheckboxValue}
									/>
								)

								const main = (
									<div>
										{group.controls.length > 1 && (
											<div className={clsx(style.rangeLabel, style.rangeLabelWithCross)}>
												<span className={style.firstUppercase}>{control.label}</span>
												{control.field in filter && !!filter[control.field] && (
													<Button
														iconBefore={<SvgCross />}
														icon={{ extraSmall: true }}
														label={translate('filterControl.remove')}
														type={{ variant: 'naked' }}
														onClick={(e) => {
															e.stopPropagation()
															control.range &&
																addDataLayerRangeFilter(control.field, control.range.min, control.range.max)
															resetFilter(control.field)
														}}
													/>
												)}
											</div>
										)}
										<FilterRange
											name={control.field}
											value={mapMinMax(value, control.formatter === 'priceDER' && calculator.to)}
											decimalPlaces={control.decimalPlaces}
											step={control.step ?? 1}
											fallback={mapMinMax(control.range, control.formatter === 'priceDER' && calculator.to)}
											unit={control.unit}
											constraints={mapMinMax(
												control.range ?? control.globalRange ?? null,
												control.formatter === 'priceDER' && calculator.to,
											)}
											onChangeDebounced={({ min, max }) =>
												min && max && addDataLayerRangeFilter(control.field, min, max)
											}
											onChange={async (value) => {
												setFilter((o) => {
													return {
														...o,
														[control.field]:
															control.formatter === 'priceDER'
																? [calculator.from(0.49)(value.min) ?? null, calculator.from(0)(value.max) ?? null]
																: [value.min ?? null, value.max ?? null],
													}
												})
											}}
											dirty={{
												min: (serverValue?.min ?? null) !== (filterValue?.min ?? null),
												max: (serverValue?.max ?? null) !== (filterValue?.max ?? null),
											}}
											formatter={control.formatter}
											coefficient={control.coefficient}
										/>
									</div>
								)

								if (control.type === 'CheckboxRange') {
									return (
										<div className={style.rangeWrapper}>
											{checkbox}
											<Collapsible open={localCheckboxValue} type="revealTopFirst">
												<div style={{ marginBottom: '.5em' }}>{main}</div>
											</Collapsible>
										</div>
									)
								}

								return <div className={style.rangeWrapper}>{main}</div>
							}
							case 'Checkbox':
								const serverCheckboxValue = info.data?.filter[control.field]

								return (
									<FilterCheckbox
										name={control.field}
										value={control.value}
										label={control.label ?? control.field}
										checked={filter[control.field] === control.value}
										onChange={(checked) => {
											setFilter((old) => {
												const patch = {
													...old,
												}

												if (checked) {
													patch[control.field] = control.value
												} else {
													delete patch[control.field]
												}

												return patch
											})

											checked &&
												control.field.startsWith('stars') &&
												addDataLayer([
													{
														event: 'filtering',
														eventLabel: {
															eventCategory: 'Filtering',
															filterCategory: 'rating',
															filterLabel: `${control.value}`,
														},
													},
												])
										}}
										count={control.count ?? 0}
										disabled={filter[control.field] !== control.value && control.count === 0}
										dirty={filter[control.field] !== serverCheckboxValue}
									/>
								)
							case 'MyDogSwitch':
								return (
									<Switch
										name={control.field}
										value={control.value}
										disabled={!dog}
										label={translate('filterControl.dogIncluded')}
										checked={!!dog ? filter[control.field] === control.value : false}
										onChange={(checked) => {
											setFilter((old) => {
												const patch = {
													...old,
												}

												if (checked) {
													patch[control.field] = control.value
												} else {
													delete patch[control.field]
												}

												return patch
											})
											addDataLayer([
												{
													event: 'filtering',
													eventLabel: {
														eventCategory: 'Filtering',
														filterCategory: 'suitable_granules',
														filterLabel: `${checked ? 'yes' : 'no'}`,
													},
												},
											])
										}}
									/>
								)
						}
					}
					const open = (openToggles[group.key] ?? false) === (group.collapsed ?? false)
					const selectedCount = group.controls.filter(
						(control) =>
							(control.field in filter && filter[control.field] !== undefined) ||
							('checkboxField' in control &&
								control.checkboxField &&
								control.checkboxField in filter &&
								filter[control.checkboxField] !== undefined),
					).length
					return (
						<React.Fragment key={group.title}>
							{group.title && (
								<div
									className={clsx(
										style.title,
										group.controls.find((control) => control.field in filter && filter[control.field] !== undefined) &&
											style.titleActive,
									)}
									onClick={() => toggleOpen(group.key)}
								>
									<span>
										<Translation tr={`productFilter.${group.key}.title`} fallback={group.title} />{' '}
										<span className={style.chevron}>
											<Chevron direction={open ? 'down' : 'right'} />
										</span>
										{selectedCount > 0 && (
											<span className={style.active}>
												<span className={style.activeNumber}>{selectedCount}</span>
											</span>
										)}
									</span>
								</div>
							)}

							<Collapsible open={open} type="revealTopFirst" hasLowerIndex>
								{group.groupControls ? (
									Object.entries(groupBy(group.controls, (item) => item.groupBy ?? '')).map(([key, controls]) => (
										<div key={key} className={style.brandLetterWrapper}>
											<div
												className={style.brandLetter}
												onClick={() => {
													toggleOpen(`${group.key}.${key}`)
												}}
											>
												<span className={style.letter}>{key}</span>{' '}
												<span className={style.letterChevron}>
													<CountIndicator
														disabled={
															controls?.reduce(
																(a: number, item) => a + ((item && 'count' in item ? item.count : 0) ?? 0),
																0,
															) === 0
														}
														count={
															controls?.filter((control) => control && 'count' in control && Number(control.count) > 0)
																.length
														}
													/>{' '}
												</span>
												<Chevron direction={openToggles[`${group.key}.${key}`] ? 'down' : 'right'} />
												{controls?.filter(
													(control) => control.field in filter && filter[control.field] !== undefined,
												) &&
													controls?.filter((control) => control.field in filter && filter[control.field] !== undefined)
														.length > 0 && (
														<>
															<span className={style.active}>
																<span className={style.activeNumber}>
																	{
																		controls?.filter(
																			(control) => control.field in filter && filter[control.field] !== undefined,
																		).length
																	}
																</span>
															</span>
														</>
													)}
											</div>
											<div>
												<Collapsible open={openToggles[`${group.key}.${key}`]} type="revealTopFirst">
													<>{controls?.map(renderControl)}</>
												</Collapsible>
											</div>
										</div>
									))
								) : (
									<>{group.controls.map(renderControl)}</>
								)}
							</Collapsible>
						</React.Fragment>
					)
				})}
			</div>
		</div>
	)

	if (notSigned || !isAllowedToFullUse) {
		return <div onClick={props.openModal}>{content}</div>
	}

	return content
}

export function ProductFilterDump(props: { control: ProductFilterControl }) {
	const { page, setPage, query, DER } = props.control

	const products = useProductFilterQuery(query, page)

	return (
		<div>
			<div>
				<pre>{products.data?.url ?? '?'}</pre>
				<div style={{ display: 'flex', alignItems: 'flex-start' }}>
					<pre>{JSON.stringify(products.data?.filter, null, 2)}</pre>
					<div>
						<div>
							stránka:
							<input
								type="number"
								value={String(page)}
								onChange={(e) => setPage(Math.max(1, e.currentTarget.valueAsNumber))}
							/>{' '}
							{products.isFetching ? 'loading...' : null}
						</div>

						{products.data?.items?.map((p) => (
							<ProductBanner 
								key={p.id} 
								product={{...p, evaluation: p.evaluation ? {...p.evaluation, remoteId: p.id} : undefined}}
								openModal={undefined} 
								dogDailyEnergyRequirement={DER ?? 1000} 
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

const rangeToMinMax = (range: unknown) => {
	const valid = z.tuple([z.number().nullable(), z.number().nullable()]).safeParse(range)

	if (valid.success) {
		return {
			min: valid.data[0],
			max: valid.data[1],
		}
	}

	return null
}

export const mapMinMax = (
	value: { min: number | null; max: number | null } | null | undefined,
	fn: false | ((diff: number) => (value: number | null | undefined, isRounded?: boolean | undefined) => number),
) => {
	if (value) {
		if (fn) {
			return {
				min: value.min !== null ? Math.floor(fn(0.49)(value.min)) : null,
				max: value.max !== null ? fn(0)(value.max) : null,
			}
		}
		return value || null
	}
	return null
}
